import React, { useState, useEffect, useMemo, useRef } from 'react';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, parseISO, isSameDay } from 'date-fns';
import { timecardApi, employeeApi, shiftApi, attendanceApi, scheduleApi, positionApi, breakTimeSettingsApi } from '../services/api';
import { Timecard, TimecardDay, Employee, UpdateTimecardDayRequest, Shift, AttendanceRecord, Schedule, Position, BreakTimeSettings } from '../types';
import { ChevronLeft, ChevronRight, Calendar, Clock, Search, RefreshCw, MoreVertical, Trash2, X, Info, MapPin } from 'lucide-react';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { useLocationContext } from '../components/Layout';

interface TimecardRow {
  date: Date;
  dateStr: string;
  shift?: Shift;
  attendanceRecord?: AttendanceRecord;
  dayData?: TimecardDay;
  shiftIndex: number; // 0 for first shift, 1 for second, etc.
  isUnscheduled?: boolean; // True if attendance without matching shift
  isManual?: boolean; // True if this is a manually added timecard row
}

const Timecards: React.FC = () => {
  const weekStartsOn = useWeekStartDay(); // Universal week start day setting
  const { selectedLocationId, locations } = useLocationContext();
  
  // Load persisted week from localStorage
  const getInitialWeek = (): Date => {
    const savedWeek = localStorage.getItem('timecards_currentWeek');
    if (savedWeek) {
      try {
        const parsedDate = new Date(savedWeek);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
      } catch (e) {
        console.error('Error parsing saved week:', e);
      }
    }
    return new Date();
  };
  
  // Load persisted employee ID from localStorage
  const getInitialEmployeeId = (): number | null => {
    const savedEmployeeId = localStorage.getItem('timecards_selectedEmployeeId');
    if (savedEmployeeId) {
      try {
        const parsedId = Number(savedEmployeeId);
        if (!isNaN(parsedId) && parsedId > 0) {
          return parsedId;
        }
      } catch (e) {
        console.error('Error parsing saved employee ID:', e);
      }
    }
    return null;
  };
  
  const [currentWeek, setCurrentWeek] = useState<Date>(getInitialWeek());
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(getInitialEmployeeId());
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [timecard, setTimecard] = useState<Timecard | null>(null);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingDays, setSavingDays] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [lastClickedField, setLastClickedField] = useState<Map<string, 'scheduledIn' | 'actualIn' | 'scheduledOut' | 'actualOut' | 'scheduledBreak' | 'actualBreak'>>(new Map());
  const [openMenuRow, setOpenMenuRow] = useState<string | null>(null); // Track which row's menu is open (row key: dateStr-shiftIndex)
  const [manualTimecardRows, setManualTimecardRows] = useState<Set<string>>(new Set()); // Track manual rows by dateStr-shiftIndex
  const [openScheduleDropdown, setOpenScheduleDropdown] = useState<string | null>(null); // Track open schedule dropdown by row key
  const [openPositionDropdown, setOpenPositionDropdown] = useState<string | null>(null); // Track open position dropdown by row key
  const [breakTimeSettings, setBreakTimeSettings] = useState<BreakTimeSettings | null>(null);
  const [showSyncStep1Modal, setShowSyncStep1Modal] = useState(false);
  const [syncStep1Data, setSyncStep1Data] = useState<Array<{ shiftId: number; date: string; inTime: string; outTime: string; breakMins: number; breakPaid: boolean }>>([]);
  const [syncStep2Data, setSyncStep2Data] = useState<Array<{ attendanceId: number; date: string; inTime: string; outTime: string | null; adjustedInTime: string | null; adjustedOutTime: string | null }>>([]);
  const [syncStep3Data, setSyncStep3Data] = useState<Array<{ attendanceId: number; date: string; inTime: string; outTime: string | null }>>([]);
  const [syncStep4Data, setSyncStep4Data] = useState<Array<{ shiftId: number; shiftDate: string; shiftInTime: string; shiftOutTime: string; shiftBreakMins: number; shiftBreakPaid: boolean; actualInTime: string | null; actualOutTime: string | null; actualInDate: string; actualOutDate: string; actualBreakMins: number | null; matchedAttendanceCount: number }>>([]);
  const [syncStep5Data, setSyncStep5Data] = useState<Array<{ shiftId: number | null; shiftDate: string | null; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualInDate: string; actualOutDate: string; actualBreakMins: number | null; matchedAttendanceCount: number }>>([]);
  const [syncStep7Data, setSyncStep7Data] = useState<Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string }>>([]);
  const [syncStep8Data, setSyncStep8Data] = useState<Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string; matchType: string }>>([]);
  const [syncStep9Data, setSyncStep9Data] = useState<Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string; matchType: string; paidInTime: string | null; paidOutTime: string | null; paidBreakMins: number | null; paidBreakPaid: boolean | null }>>([]);
  const [syncStep10Data, setSyncStep10Data] = useState<Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string; matchType: string; paidInTime: string | null; paidOutTime: string | null; paidBreakMins: number | null; paidBreakPaid: boolean | null; totalHours: number | null; status: string }>>([]);
  const employeeListRef = useRef<HTMLDivElement>(null);
  const selectedEmployeeRef = useRef<HTMLButtonElement>(null);
  const hasScrolledToInitialEmployee = useRef(false);
  const isSyncingRef = useRef(false); // Track if sync is in progress to prevent concurrent syncs
  const [showApprovedBlockMessage, setShowApprovedBlockMessage] = useState(false);
  const [showUnapproveConfirm, setShowUnapproveConfirm] = useState(false);
  const [unapproveError, setUnapproveError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ dateStr: string; shiftIndex: number; dayDataId?: number; dateLabel: string } | null>(null);
  const [overlapError, setOverlapError] = useState<string | null>(null);
  const [otherLocationTotals, setOtherLocationTotals] = useState<Array<{ locationId: number; locationName: string; totalHours: number }>>([]);

  // Calculate week start using universal week start day setting
  const weekStart = useMemo(() => startOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);
  const weekEnd = useMemo(() => endOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);

  // Generate array of dates for the week
  const weekDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(addDays(weekStart, i));
    }
    return dates;
  }, [weekStart]);

  // Persist current week to localStorage
  useEffect(() => {
    localStorage.setItem('timecards_currentWeek', currentWeek.toISOString());
  }, [currentWeek]);

  // Persist selected employee ID to localStorage
  useEffect(() => {
    if (selectedEmployeeId !== null) {
      localStorage.setItem('timecards_selectedEmployeeId', selectedEmployeeId.toString());
    } else {
      localStorage.removeItem('timecards_selectedEmployeeId');
    }
  }, [selectedEmployeeId]);


  // Fetch employees, schedules, and positions
  useEffect(() => {
        const fetchData = async () => {
          try {
            const [employeesData, schedulesData, positionsData, breakTimeData] = await Promise.all([
              employeeApi.getAll(),
              scheduleApi.getAll(),
              positionApi.getAll(),
              breakTimeSettingsApi.get().catch(() => ({ id: undefined, isPaid: false, rules: [] }))
            ]);
            const activeEmployees = employeesData.filter(e => e.isActive);
            setEmployees(activeEmployees);
            setSchedules(schedulesData);
            setPositions(positionsData);
            setBreakTimeSettings(breakTimeData || { id: undefined, isPaid: false, rules: [] });
          } catch (error) {
            console.error('Error fetching data:', error);
          }
        };
    fetchData();
  }, []);

  // Fetch shifts for the employee and week
  useEffect(() => {
    if (!selectedEmployeeId || !selectedLocationId || selectedLocationId === 0) {
      setShifts([]);
      return;
    }

    const fetchShifts = async () => {
      try {
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        const weekEndStr = format(weekEnd, 'yyyy-MM-dd');
        const data = await shiftApi.getAll({
          employeeId: selectedEmployeeId,
          startDate: weekStartStr,
          endDate: weekEndStr,
          locationId: selectedLocationId || undefined
        });
        setShifts(data);
      } catch (error) {
        console.error('Error fetching shifts:', error);
        setShifts([]);
      }
    };

    fetchShifts();
  }, [selectedEmployeeId, selectedLocationId, weekStart, weekEnd]);

  // Fetch attendance records for the employee and week
  useEffect(() => {
    if (!selectedEmployeeId || !selectedLocationId || selectedLocationId === 0) {
      setAttendanceRecords([]);
      return;
    }

    const fetchAttendance = async () => {
      try {
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        const weekEndStr = format(weekEnd, 'yyyy-MM-dd');
        const data = await attendanceApi.getAll({
          employeeId: selectedEmployeeId,
          startDate: weekStartStr,
          endDate: weekEndStr,
          locationId: selectedLocationId || undefined
        });
        setAttendanceRecords(data);
      } catch (error) {
        console.error('Error fetching attendance records:', error);
        setAttendanceRecords([]);
      }
    };

    fetchAttendance();
  }, [selectedEmployeeId, selectedLocationId, weekStart, weekEnd]);

  // Fetch timecard when employee or week changes (without syncing)
  useEffect(() => {
    if (!selectedEmployeeId || !selectedLocationId || selectedLocationId === 0) {
      setTimecard(null);
      return;
    }

    const fetchTimecard = async () => {
      setLoading(true);
      try {
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        const weekEndStr = format(weekEnd, 'yyyy-MM-dd');
        
        // Fetch shifts (in case shifts were added/removed/changed)
        const shiftsData = await shiftApi.getAll({
          employeeId: selectedEmployeeId,
          startDate: weekStartStr,
          endDate: weekEndStr,
          locationId: selectedLocationId || undefined
        });
        setShifts(shiftsData);
        
        // Fetch attendance records (in case attendance was added/adjusted)
        const attendanceData = await attendanceApi.getAll({
          employeeId: selectedEmployeeId,
          startDate: weekStartStr,
          endDate: weekEndStr,
          locationId: selectedLocationId || undefined
        });
        setAttendanceRecords(attendanceData);
        
        // Conditional sync: only run for this Week+Location+Employee if sync is needed (no timecard/days, or shifts/attendance created or modified after last sync)
        let syncRan = false;
        if (!isSyncingRef.current) {
          try {
            isSyncingRef.current = true;
            const syncCheck = await timecardApi.checkSyncNeeded(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined);
            if (syncCheck.syncNeeded) {
              syncRan = true;
              await timecardApi.sync(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined);
            }
          } catch (syncError: any) {
            console.error('[Timecard] Sync error:', syncError?.response?.data?.message || syncError?.message, syncError?.response?.data);
            // Continue with normal fetch even if sync check fails
          } finally {
            isSyncingRef.current = false;
          }
        }
        
        // Fetch the timecard (after potential sync)
        const data = await timecardApi.getByEmployeeAndWeek(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined);
        console.log('[Timecard] fetchTimecard (initial/refresh):', {
          employeeId: selectedEmployeeId,
          locationId: selectedLocationId,
          weekStartStr,
          syncRan,
          timecardId: data?.id,
          daysCount: data?.days?.length,
          manualDays: data?.days?.filter(d => d.mode === 'Manual').map(d => ({ id: d.id, date: d.date, paidIn: d.paidIn, paidOut: d.paidOut, paidBreakMinutes: d.paidBreakMinutes }))
        });
        if (data) {
          setTimecard(data);
        } else {
          // Create empty timecard structure as fallback
          const emptyTimecard: Timecard = {
            id: 0,
            employeeId: selectedEmployeeId,
            weekStartDate: weekStartStr,
            createdAt: new Date().toISOString(),
            days: weekDates.map(date => ({
              id: 0,
              date: format(date, 'yyyy-MM-dd'),
              mode: 'Auto' as const,
              extras: 0,
              totalHours: 0
            }))
          };
          setTimecard(emptyTimecard);
        }
      } catch (error) {
        console.error('Error fetching timecard:', error);
        setTimecard(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTimecard();
  }, [selectedEmployeeId, selectedLocationId, weekStart, weekDates]);

  // Fetch weekly totals for other locations (where employee has timecard with hours > 0)
  useEffect(() => {
    if (!selectedEmployeeId || !selectedLocationId || !locations?.length) {
      setOtherLocationTotals([]);
      return;
    }

    const otherLocations = locations.filter(loc => loc.id !== selectedLocationId);
    if (otherLocations.length === 0) {
      setOtherLocationTotals([]);
      return;
    }

    const fetchOtherTotals = async () => {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const results: Array<{ locationId: number; locationName: string; totalHours: number }> = [];
      await Promise.all(
        otherLocations.map(async (loc) => {
          try {
            const tc = await timecardApi.getByEmployeeAndWeek(selectedEmployeeId, weekStartStr, loc.id);
            const total = tc?.days?.reduce((sum, d) => sum + (d.totalHours || 0), 0) ?? 0;
            if (total > 0) {
              results.push({ locationId: loc.id, locationName: loc.name, totalHours: total });
            }
          } catch {
            // Ignore 404 or errors for locations without timecard
          }
        })
      );
      setOtherLocationTotals(results);
    };

    fetchOtherTotals();
  }, [selectedEmployeeId, selectedLocationId, weekStart, locations]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuRow !== null) {
        setOpenMenuRow(null);
      }
      if (openScheduleDropdown !== null) {
        setOpenScheduleDropdown(null);
      }
      if (openPositionDropdown !== null) {
        setOpenPositionDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuRow, openScheduleDropdown, openPositionDropdown]);

  // Helper function to check if a shift and attendance record overlap
  const doesShiftOverlapAttendance = (shift: Shift, attendance: AttendanceRecord): boolean => {
    const shiftStart = parseISO(shift.startTime);
    const shiftEnd = parseISO(shift.endTime);
    const clockIn = parseISO(attendance.clockInTime);
    const clockOut = attendance.clockOutTime ? parseISO(attendance.clockOutTime) : null;

    // Check overlap conditions:
    // 1. Scheduled IN between ClockIn AND ClockOut OR
    // 2. ScheduledOut between ClockIn and ClockOut OR
    // 3. ClockIn between ScheduledIN and ScheduledOut OR
    // 4. ClockOut between ScheduledIN and ScheduledOut
    
    if (clockOut) {
      // Both times exist - check for any overlap
      // Shift starts during attendance OR attendance starts during shift
      return (shiftStart >= clockIn && shiftStart <= clockOut) ||
             (shiftEnd >= clockIn && shiftEnd <= clockOut) ||
             (clockIn >= shiftStart && clockIn <= shiftEnd) ||
             (clockOut >= shiftStart && clockOut <= shiftEnd);
    } else {
      // No clock out - check if shift overlaps with clock in time
      return (shiftStart <= clockIn && shiftEnd >= clockIn) ||
             (clockIn >= shiftStart && clockIn <= shiftEnd);
    }
  };

  // Extract time from datetime string
  const extractTime = (datetimeStr: string | null | undefined): string => {
    if (!datetimeStr) return '--:--';
    try {
      // Handle both DateTime strings (yyyy-MM-ddTHH:mm:ss) and time strings (HH:mm)
      const date = parseISO(datetimeStr);
      if (!isNaN(date.getTime())) {
        return format(date, 'HH:mm');
      }
      // If parseISO fails, try parsing as time string directly
      if (datetimeStr.match(/^\d{2}:\d{2}$/)) {
        return datetimeStr;
      }
      return '--:--';
    } catch {
      return '--:--';
    }
  };

  // Build rows for the table - build from timecard days (DB records) if they exist, otherwise from shifts/attendance
  const tableRows = useMemo(() => {
    const rows: TimecardRow[] = [];
    
    weekDates.forEach(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      
      // Get all timecard day records for this date (can be multiple now)
      // Use date comparison that is robust to both "yyyy-MM-dd" and full DateTime strings.
      const dayDataRecords =
        timecard?.days.filter(d => {
          if (!d.date) return false;
          try {
            // If it's already a plain date string, compare directly first
            if (/^\d{4}-\d{2}-\d{2}$/.test(d.date)) {
              return d.date === dateStr;
            }
            // Fallback: parse as Date/DateTime and compare by day
            const dayDate = parseISO(d.date);
            return !isNaN(dayDate.getTime()) && isSameDay(dayDate, date);
          } catch {
            return false;
          }
        }) || [];
      
      // Get shifts for this date
      const dayShifts = shifts.filter(shift => {
        const shiftDate = parseISO(shift.startTime);
        return isSameDay(shiftDate, date);
      });

      // Get attendance records for this date
      const dayAttendance = attendanceRecords.filter(attendance => {
        const attendanceDate = parseISO(attendance.clockInTime);
        return isSameDay(attendanceDate, date);
      });

      // If we have timecard day records, build rows from them (mirror DB),
      // but when a specific location is selected, only show actual times that
      // have matching attendance in that location.
      if (dayDataRecords.length > 0) {
        dayDataRecords.forEach((dayData, index) => {
          // Try to find matching shift based on scheduled times
          let matchedShift: Shift | undefined;
          if (dayData.scheduledIn) {
            // Find shift that matches scheduled times (compare time portion)
            // Extract time from DateTime string if needed
            const scheduledInTime = extractTime(dayData.scheduledIn);
            matchedShift = dayShifts.find(shift => {
              const shiftDate = parseISO(shift.startTime);
              const shiftTime = format(shiftDate, 'HH:mm');
              return shiftTime === scheduledInTime;
            });
          }

          // Try to find matching attendance based on actual times
          let matchedAttendance: AttendanceRecord | undefined;
          if (dayData.actualIn) {
            // Extract time from DateTime string if needed
            const actualInTime = extractTime(dayData.actualIn);
            matchedAttendance = dayAttendance.find(attendance => {
              const attendanceDate = parseISO(attendance.clockInTime);
              const attendanceTime = format(attendanceDate, 'HH:mm');
              return attendanceTime === actualInTime;
            });
          }

          // When a specific location is selected but there is no matching
          // attendance at that location for this row, hide actual/paid times
          // from the timecard day for display so that Actual Time values
          // reflect only the selected location.
          let displayDayData = dayData;
          if (
            selectedLocationId !== null &&
            selectedLocationId !== 0 &&
            !matchedAttendance &&
            // For manual timecard days we always want to show Paid values,
            // even if there is no attendance for the selected location.
            dayData.mode !== 'Manual'
          ) {
            displayDayData = {
              ...dayData,
              actualIn: null,
              actualOut: null,
              actualBreakMinutes: null,
              paidIn: null,
              paidOut: null,
              paidBreakMinutes: null,
            };
          }

          // Determine if unscheduled (no scheduled times but has actual times)
          const isUnscheduled =
            !displayDayData.scheduledIn &&
            !displayDayData.scheduledOut &&
            !!displayDayData.actualIn;

          // Determine if this should be treated as a "manual" row in the UI.
          // We want manual rows (added via "Add Timecard") to:
          // - Start with Shift and Actual sections blank
          // - Have editable Paid In/Out/Break fields even when there are no Actuals
          //
          // Prefer the explicit mode flag when present, but also treat completely
          // blank rows with no shift/attendance/actuals as manual.
          const isManualRow =
            displayDayData.mode === 'Manual' ||
            (!matchedShift &&
              !matchedAttendance &&
              !displayDayData.scheduledIn &&
              !displayDayData.scheduledOut &&
              !displayDayData.actualIn &&
              !displayDayData.actualOut);

          rows.push({
            date,
            dateStr,
            shift: matchedShift,
            attendanceRecord: matchedAttendance,
            dayData: displayDayData,
            shiftIndex: index,
            isUnscheduled,
            // Mark rows that should be handled as manual in the UI
            // so that Shift and Actual sections stay blank and
            // Paid boxes are always editable.
            isManual: isManualRow
          });
        });
      } else {
        // No timecard day records yet - build from shifts/attendance (for initial display before sync)
        const matchedAttendanceIds = new Set<number>();
        let shiftIndex = 0;

        // Process each shift - find matching attendance records
        dayShifts.forEach(shift => {
          // Find attendance record that overlaps with this shift
          const matchedAttendance = dayAttendance.find(attendance => {
            if (matchedAttendanceIds.has(attendance.id)) return false; // Already matched
            return doesShiftOverlapAttendance(shift, attendance);
          });

          if (matchedAttendance) {
            // Shift has matching attendance - show together
            matchedAttendanceIds.add(matchedAttendance.id);
            rows.push({
              date,
              dateStr,
              shift,
              attendanceRecord: matchedAttendance,
              shiftIndex: shiftIndex++
            });
          } else {
            // Shift without matching attendance - show with blank actual times
            rows.push({
              date,
              dateStr,
              shift,
              shiftIndex: shiftIndex++
            });
          }
        });

        // Group unscheduled attendance records (those without matching shifts) and combine them
        const unscheduledAttendance = dayAttendance.filter(attendance => 
          !matchedAttendanceIds.has(attendance.id)
        );

        if (unscheduledAttendance.length > 0) {
          // Sort by clock-in time to get first and last
          const sortedUnscheduled = [...unscheduledAttendance].sort((a, b) => {
            const timeA = parseISO(a.clockInTime).getTime();
            const timeB = parseISO(b.clockInTime).getTime();
            return timeA - timeB;
          });

          // Get first clock-in and last clock-out
          const firstClockIn = sortedUnscheduled[0].clockInTime;
          const lastClockOut = sortedUnscheduled
            .filter(a => a.clockOutTime)
            .sort((a, b) => {
              const timeA = parseISO(a.clockOutTime!).getTime();
              const timeB = parseISO(b.clockOutTime!).getTime();
              return timeB - timeA; // Descending to get last
            })[0]?.clockOutTime;

          // Sum break minutes from all records
          let totalBreakMinutes = sortedUnscheduled.reduce((sum, record) => {
            return sum + (record.breakMinutes || 0);
          }, 0);

          // Calculate gaps between consecutive records (clock-out to next clock-in)
          // These gaps are also considered break time
          for (let i = 0; i < sortedUnscheduled.length - 1; i++) {
            const currentRecord = sortedUnscheduled[i];
            const nextRecord = sortedUnscheduled[i + 1];
            
            if (currentRecord.clockOutTime && nextRecord.clockInTime) {
              const clockOutTime = parseISO(currentRecord.clockOutTime);
              const nextClockInTime = parseISO(nextRecord.clockInTime);
              
              // Calculate minutes between clock-out and next clock-in
              const gapMinutes = Math.max(0, (nextClockInTime.getTime() - clockOutTime.getTime()) / (1000 * 60));
              totalBreakMinutes += gapMinutes;
            }
          }

          // Create a combined attendance record
          const combinedAttendance: AttendanceRecord = {
            ...sortedUnscheduled[0], // Use first record as base
            id: sortedUnscheduled[0].id, // Keep first record's ID for reference
            clockInTime: firstClockIn,
            clockOutTime: lastClockOut || undefined,
            breakMinutes: totalBreakMinutes
          };

          // Show combined unscheduled attendance as a single row
          rows.push({
            date,
            dateStr,
            attendanceRecord: combinedAttendance,
            shiftIndex: shiftIndex++,
            isUnscheduled: true
          });
        }

        // If no shifts and no attendance, show one row with "Unscheduled"
        if (dayShifts.length === 0 && dayAttendance.length === 0) {
          rows.push({
            date,
            dateStr,
            shiftIndex: 0
          });
        }
      }
      
      // Add manual timecard rows for this date (if they don't have a dayData record)
      manualTimecardRows.forEach(manualRowKey => {
        const [rowDateStr, rowShiftIndexStr] = manualRowKey.split('-');
        if (rowDateStr === dateStr) {
          const rowShiftIndex = parseInt(rowShiftIndexStr);
          // Only add if this manual row doesn't already exist as a dayData row
          const existingRow = rows.find(r => r.dateStr === dateStr && r.shiftIndex === rowShiftIndex);
          if (!existingRow) {
            rows.push({
              date,
              dateStr,
              dayData: timecard?.days.find(d => d.date === dateStr),
              shiftIndex: rowShiftIndex,
              isManual: true
            });
          }
        }
      });
    });

    // Helper to get sortable time for a row: Paid In (for manual) > Actual In > empty
    // Manual rows use paidIn for ordering; others use actualIn. Empty sorts last (bottom).
    const getSortTime = (row: TimecardRow): string => {
      const paidIn = row.dayData?.paidIn ? extractTime(row.dayData.paidIn) : null;
      if (paidIn && paidIn !== '--:--') {
        const dateStr = row.dayData?.date ? (row.dayData.date.replace(/T.*/, '') || row.dateStr) : row.dateStr;
        return `${dateStr}T${paidIn}:00`;
      }
      if (row.dayData?.actualIn) {
        const actualInStr = row.dayData.actualIn.trim();
        if (actualInStr.includes(' ') && !actualInStr.includes('T')) {
          return actualInStr.replace(' ', 'T').replace(/\.\d+$/, '');
        }
        return actualInStr.replace(/\.\d+$/, '');
      }
      if (row.attendanceRecord?.clockInTime) {
        return row.attendanceRecord.clockInTime;
      }
      return ''; // No time - sorts last (bottom)
    };
    
    // Helper to get shift In time value for sorting
    const getShiftInTime = (row: TimecardRow): number => {
      if (row.shift) {
        const shiftTime = parseISO(row.shift.startTime);
        return shiftTime.getTime();
      }
      return Infinity; // Unscheduled records
    };

    // Sort rows: by date, then by Paid In / Actual In (rows with no time always last/bottom)
    rows.sort((a, b) => {
      if (a.dateStr !== b.dateStr) {
        return a.dateStr.localeCompare(b.dateStr);
      }
      const aTime = getSortTime(a);
      const bTime = getSortTime(b);
      if (aTime && bTime) return aTime.localeCompare(bTime);
      if (aTime && !bTime) return -1; // a first (has time)
      if (!aTime && bTime) return 1;  // b first (a has no time → a goes last)
      // Both empty: scheduled first (by shift time), manual rows last
      const aShift = getShiftInTime(a);
      const bShift = getShiftInTime(b);
      if (aShift === Infinity && bShift === Infinity) return (a.dayData?.id ?? 0) - (b.dayData?.id ?? 0);
      return aShift - bShift;
    });

    return rows;
  }, [weekDates, shifts, attendanceRecords, timecard, manualTimecardRows]);

  // Handle week navigation
  const handlePreviousWeek = () => {
    setCurrentWeek(subWeeks(currentWeek, 1));
  };

  const handleNextWeek = () => {
    setCurrentWeek(addWeeks(currentWeek, 1));
  };

  const handleTodayWeek = () => {
    setCurrentWeek(new Date());
  };

  // Helper function to refetch timecard data from DB
  const refetchTimecardData = async () => {
    if (!selectedEmployeeId) return;
    
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const weekEndStr = format(weekEnd, 'yyyy-MM-dd');
      
      // Refetch shifts and attendance
      const shiftsData = await shiftApi.getAll({
        employeeId: selectedEmployeeId,
        startDate: weekStartStr,
        endDate: weekEndStr
      });
      setShifts(shiftsData);
      
      const attendanceData = await attendanceApi.getAll({
        employeeId: selectedEmployeeId,
        startDate: weekStartStr,
        endDate: weekEndStr
      });
      setAttendanceRecords(attendanceData);
      
        // Refetch timecard to get updated data from DB
        const updatedTimecard = await timecardApi.getByEmployeeAndWeek(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined);
        console.log('[Timecard] refetchTimecardData:', {
          locationId: selectedLocationId,
          timecardId: updatedTimecard?.id,
          daysCount: updatedTimecard?.days?.length,
          manualDays: updatedTimecard?.days?.filter(d => d.mode === 'Manual').map(d => ({ id: d.id, date: d.date, paidIn: d.paidIn, paidOut: d.paidOut, paidBreakMinutes: d.paidBreakMinutes }))
        });
        if (updatedTimecard) {
          setTimecard(updatedTimecard);
        }
    } catch (error) {
      console.error('Error refetching timecard data:', error);
    }
  };

  // Re-sync: run sync unconditionally for this Week + Location + Employee (no check), then refetch
  const handleResync = async () => {
    if (!selectedEmployeeId || timecard?.isApproved) return;
    
    setLoading(true);
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      await timecardApi.sync(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined);
      await refetchTimecardData();
    } catch (error) {
      console.error('Error re-syncing timecard:', error);
      alert('Error re-syncing: ' + (error as any)?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Handle timecard approval - once approved, sync should not be run again
  const handleApproveTimecard = async () => {
    if (!timecard || !selectedEmployeeId) return;

    setLoading(true);
    try {
      await timecardApi.approve(timecard.id);

      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const updatedTimecard = await timecardApi.getByEmployeeAndWeek(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined);
      if (updatedTimecard) {
        setTimecard(updatedTimecard);
      }
    } catch (error) {
      console.error('Error approving timecard:', error);
      alert('Error approving timecard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnapproveTimecard = async () => {
    if (!timecard || !selectedEmployeeId) return;

    setLoading(true);
    try {
      setUnapproveError(null);
      await timecardApi.unapprove(timecard.id);

      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const updatedTimecard = await timecardApi.getByEmployeeAndWeek(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined);
      if (updatedTimecard) {
        setTimecard(updatedTimecard);
        setShowApprovedBlockMessage(false);
        setShowUnapproveConfirm(false);
      }
    } catch (error) {
      console.error('Error unapproving timecard:', error);
      setUnapproveError('Failed to unapprove timecard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper: parse "HH:mm" to minutes since midnight; return null if invalid
  const timeToMinutes = (timeStr: string | null | undefined): number | null => {
    if (!timeStr || timeStr === '--:--') return null;
    const parts = timeStr.trim().split(':');
    if (parts.length < 2) return null;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  };

  // Check if two time ranges overlap (handles overnight shifts)
  const timeRangesOverlap = (
    aIn: number | null,
    aOut: number | null,
    bIn: number | null,
    bOut: number | null
  ): boolean => {
    if (aIn == null || aOut == null || bIn == null || bOut == null) return false;
    let aEnd = aOut;
    let bEnd = bOut;
    if (aEnd <= aIn) aEnd += 24 * 60;
    if (bEnd <= bIn) bEnd += 24 * 60;
    return aIn < bEnd && bIn < aEnd;
  };

  // Helper to convert time string (HH:mm) to DateTime string (yyyy-MM-ddTHH:mm:ss) for API
  const timeToDateTime = (timeStr: string | null | undefined, date: Date): string | undefined => {
    if (!timeStr || timeStr === '--:--') return undefined;
    try {
      // If already a DateTime string, return as-is
      if (timeStr.includes('T')) {
        return timeStr;
      }
      // If it's a time string (HH:mm), combine with date
      const [hours, minutes] = timeStr.split(':').map(Number);
      if (!isNaN(hours) && !isNaN(minutes)) {
        const dateTime = new Date(date);
        dateTime.setHours(hours, minutes, 0, 0);
        return format(dateTime, "yyyy-MM-dd'T'HH:mm:ss");
      }
      return undefined;
    } catch {
      return undefined;
    }
  };

  // Calculate break minutes from shift (if unpaidHours is available)
  const getBreakMinutes = (shift: Shift | undefined): number => {
    if (!shift || !shift.unpaidHours) return 0;
    return Math.round(shift.unpaidHours * 60);
  };

  // Helper function to get 2-letter code from name
  // If 2+ words: First letter of first word + First letter of second word (e.g., "Accounting Officer" → "AO")
  // If single word: First 2 letters (e.g., "Manager" → "MA")
  const getTwoLetterCode = (name: string | null | undefined): string => {
    if (!name) return '';
    const trimmed = name.trim();
    if (!trimmed) return '';
    
    // Split by spaces and filter out empty strings
    const words = trimmed.split(/\s+/).filter(w => w.length > 0);
    
    if (words.length >= 2) {
      // Two or more words: First letter of first word + First letter of second word
      return (words[0][0] + words[1][0]).toUpperCase();
    } else if (words.length === 1) {
      // Single word: First 2 letters
      const word = words[0].toUpperCase();
      return word.length >= 2 ? word.substring(0, 2) : word.padEnd(2, 'X');
    }
    
    return '';
  };

  // Get available schedules for the selected employee
  // Include schedules from employee profile AND from shifts (in case schedule was removed from profile but shift still references it)
  const availableSchedules = useMemo(() => {
    if (!selectedEmployeeId) return [];
    const employee = employees.find(emp => emp.id === selectedEmployeeId);
    const scheduleIds = employee?.profile?.scheduleIds || [];
    
    // Get schedule IDs from shifts for the current week
    const shiftScheduleIds = new Set<number>();
    shifts.forEach(shift => {
      if (shift.scheduleId) {
        shiftScheduleIds.add(shift.scheduleId);
      }
    });
    
    // Combine profile schedules and shift schedules
    const allScheduleIds = new Set([...scheduleIds, ...Array.from(shiftScheduleIds)]);
    
    return schedules.filter(s => s.isActive && allScheduleIds.has(s.id));
  }, [selectedEmployeeId, employees, schedules, shifts]);

  // Get available positions for the selected employee
  // Include positions from employee profile AND from shifts (in case position was removed from profile but shift still references it)
  const availablePositions = useMemo(() => {
    if (!selectedEmployeeId) return [];
    const employee = employees.find(emp => emp.id === selectedEmployeeId);
    const mainPositionId = employee?.profile?.mainPositionId;
    const subPositionIds = employee?.profile?.subPositionIds || [];
    const profilePositionIds = [mainPositionId, ...subPositionIds].filter((id): id is number => id !== undefined && id !== null);
    
    // Get position IDs from shifts for the current week
    const shiftPositionIds = new Set<number>();
    shifts.forEach(shift => {
      if (shift.positionId) {
        shiftPositionIds.add(shift.positionId);
      }
    });
    
    // Combine profile positions and shift positions
    const allPositionIds = new Set([...profilePositionIds, ...Array.from(shiftPositionIds)]);
    
    return positions.filter(p => p.isActive && allPositionIds.has(p.id));
  }, [selectedEmployeeId, employees, positions, shifts]);

  const APPROVED_BLOCK_MESSAGE = 'Timecard is already approved. Please unapprove first to make changes.';

  // Handle adding manual timecard row
  const handleAddManualTimecard = async (dateStr: string) => {
    if (!selectedEmployeeId) return;
    if (timecard?.isApproved) {
      setShowApprovedBlockMessage(true);
      return;
    }
    setOpenMenuRow(null);
    
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');

      // Ensure timecard exists
      if (!timecard || !timecard.id || timecard.id === 0) {
        // Create new timecard with a manual day entry
        const newTimecard = await timecardApi.create({
          employeeId: selectedEmployeeId,
          weekStartDate: weekStartStr,
          days: [{
            date: dateStr,
            mode: 'Manual',
            extras: 0,
            totalHours: 0
          }]
        });
        setTimecard(newTimecard);
      } else {
        // Timecard exists - always add a new manual day row for this date
        await timecardApi.addDay(timecard.id, {
          date: dateStr,
          mode: 'Manual',
          extras: 0,
          totalHours: 0
        });

        // Refetch timecard to get the updated data (including the new row)
        await refetchTimecardData();
      }
    } catch (error: any) {
      console.error('Error adding manual timecard:', error);
      if (error.response?.status === 409) {
        alert('A timecard entry for this date already exists.');
      } else {
        alert('Failed to add timecard. Please try again.');
      }
    }
  };

  // Handle deleting manual timecard row
  const handleDeleteManualTimecard = async (dateStr: string, shiftIndex: number, dayDataId?: number) => {
    if (timecard?.isApproved) {
      setShowApprovedBlockMessage(true);
      return;
    }
    setOpenMenuRow(null);
    const manualRowKey = `${dateStr}-${shiftIndex}`;
    setManualTimecardRows(prev => {
      const newSet = new Set(prev);
      newSet.delete(manualRowKey);
      return newSet;
    });
    if (timecard?.id && dayDataId) {
      try {
        await timecardApi.deleteDay(timecard.id, dayDataId);
        await refetchTimecardData();
      } catch (error) {
        console.error('Error deleting timecard day:', error);
        alert('Failed to delete timecard.');
      }
    }
  };

  // Handle day update
  const handleDayUpdate = async (dateStr: string, shiftId: number | undefined, updates: Partial<TimecardDay>, dayDataId?: number) => {
    if (!timecard || !selectedEmployeeId) return;
    if (timecard.isApproved) {
      setShowApprovedBlockMessage(true);
      return;
    }

    // If dayDataId is provided, use it to find the specific dayData record
    // Otherwise, fall back to finding by dateStr (for backward compatibility)
    const dayData = dayDataId 
      ? timecard.days.find(d => d.id === dayDataId)
      : timecard.days.find(d => d.date === dateStr);

    if (!timecard.id || timecard.id === 0) {
      // Create new timecard with updated day data
      try {
        const daysToCreate = timecard.days.map(d => {
          if (d.date === dateStr) {
            return { ...d, ...updates };
          }
          return d;
        });

        const newTimecard = await timecardApi.create({
          employeeId: selectedEmployeeId,
          weekStartDate: format(weekStart, 'yyyy-MM-dd'),
          days: daysToCreate.map(d => ({
            date: d.date,
            mode: d.mode,
            scheduleCode: d.scheduleCode || undefined,
            positionCode: d.positionCode || undefined,
            extras: d.extras,
            totalHours: d.totalHours,
            paidIn: d.paidIn || undefined,
            paidOut: d.paidOut || undefined,
            paidBreakMinutes: d.paidBreakMinutes || undefined,
            paidBreakPaid: d.paidBreakPaid
          }))
        });
        setTimecard(newTimecard);
      } catch (error) {
        console.error('Error creating timecard:', error);
      }
    } else if (dayData) {
      // Overlap validation: block save if new paid range overlaps with other rows for same date
      if (updates.paidIn !== undefined || updates.paidOut !== undefined) {
        const effectivePaidIn = extractTime(updates.paidIn ?? dayData.paidIn ?? undefined);
        const effectivePaidOut = extractTime(updates.paidOut ?? dayData.paidOut ?? undefined);
        const myIn = timeToMinutes(effectivePaidIn);
        const myOut = timeToMinutes(effectivePaidOut);
        if (myIn != null && myOut != null) {
          const sameDateDays = timecard.days.filter(d => {
            if (!d.date) return false;
            const dDate = /^\d{4}-\d{2}-\d{2}$/.test(d.date) ? d.date : (d.date.replace(/T.*/, '') ?? '');
            if (dDate !== dateStr) return false;
            if (dayDataId && d.id === dayDataId) return false;
            return true;
          });
          for (const other of sameDateDays) {
            const otherIn = timeToMinutes(extractTime(other.paidIn));
            const otherOut = timeToMinutes(extractTime(other.paidOut));
            if (otherIn != null && otherOut != null && timeRangesOverlap(myIn, myOut, otherIn, otherOut)) {
              setOverlapError(`The Paid In/Out range overlaps with another timecard for ${format(parseISO(dateStr), 'EEEE MMM dd')}. Please adjust the times.`);
              return;
            }
          }
        }
      }

      // Update existing day
      setSavingDays(prev => new Set(prev).add(dayData.id));
      try {
        // Convert null values to undefined for API call and only include defined fields
        // Convert time strings (HH:mm) to DateTime strings (yyyy-MM-ddTHH:mm:ss) for API
        const apiUpdates: UpdateTimecardDayRequest = {};
        if (updates.scheduledIn !== undefined) apiUpdates.scheduledIn = timeToDateTime(updates.scheduledIn ?? undefined, dayData.date ? parseISO(dayData.date) : new Date());
        if (updates.scheduledOut !== undefined) apiUpdates.scheduledOut = timeToDateTime(updates.scheduledOut ?? undefined, dayData.date ? parseISO(dayData.date) : new Date());
        if (updates.scheduledBreakMinutes !== undefined) apiUpdates.scheduledBreakMinutes = updates.scheduledBreakMinutes ?? undefined;
        if (updates.actualIn !== undefined) apiUpdates.actualIn = timeToDateTime(updates.actualIn ?? undefined, dayData.date ? parseISO(dayData.date) : new Date());
        if (updates.actualOut !== undefined) apiUpdates.actualOut = timeToDateTime(updates.actualOut ?? undefined, dayData.date ? parseISO(dayData.date) : new Date());
        if (updates.actualBreakMinutes !== undefined) apiUpdates.actualBreakMinutes = updates.actualBreakMinutes ?? undefined;
        const dayDate = dayData.date ? parseISO(dayData.date) : new Date();
        if (updates.paidIn !== undefined) apiUpdates.paidIn = timeToDateTime(updates.paidIn ?? undefined, dayDate);
        if (updates.paidOut !== undefined) {
          const paidInStr = extractTime(updates.paidIn ?? dayData.paidIn ?? undefined);
          const paidOutStr = extractTime(updates.paidOut ?? undefined);
          const isOvernight = paidInStr && paidOutStr && paidInStr !== '--:--' && paidOutStr !== '--:--' && (() => {
            const [inH, inM] = paidInStr.split(':').map(Number);
            const [outH, outM] = paidOutStr.split(':').map(Number);
            return outH * 60 + outM <= inH * 60 + inM;
          })();
          apiUpdates.paidOut = timeToDateTime(updates.paidOut ?? undefined, isOvernight ? addDays(dayDate, 1) : dayDate);
        }
        if (updates.paidBreakMinutes !== undefined) apiUpdates.paidBreakMinutes = updates.paidBreakMinutes ?? undefined;
        if (updates.paidBreakPaid !== undefined) apiUpdates.paidBreakPaid = updates.paidBreakPaid;
        if (updates.scheduleCode !== undefined) apiUpdates.scheduleCode = updates.scheduleCode ?? undefined;
        if (updates.positionCode !== undefined) apiUpdates.positionCode = updates.positionCode ?? undefined;
        if (updates.extras !== undefined) apiUpdates.extras = updates.extras;
        
        // Recalculate total hours and default paidBreakMinutes from Break rule matrix if paid times changed
        if (updates.paidIn !== undefined || updates.paidOut !== undefined || updates.paidBreakMinutes !== undefined || updates.paidBreakPaid !== undefined) {
          // Extract time portion from DateTime strings if needed
          const paidInTime = extractTime(updates.paidIn ?? dayData.paidIn ?? undefined);
          const paidOutTime = extractTime(updates.paidOut ?? dayData.paidOut ?? undefined);
          const paidBreak = updates.paidBreakMinutes ?? dayData.paidBreakMinutes ?? 0;
          const paidBreakPaid = updates.paidBreakPaid !== undefined ? updates.paidBreakPaid : dayData.paidBreakPaid ?? false;
          
          if (paidInTime && paidOutTime && paidInTime !== '--:--' && paidOutTime !== '--:--') {
            const [inHours, inMins] = paidInTime.split(':').map(Number);
            const [outHours, outMins] = paidOutTime.split(':').map(Number);
            const inMinutes = inHours * 60 + inMins;
            let outMinutes = outHours * 60 + outMins;
            // Overnight shift: Paid Out < Paid In means Out is next day
            if (outMinutes <= inMinutes) {
              outMinutes += 24 * 60;
            }
            const grossHours = (outMinutes - inMinutes) / 60;

            // Default paidBreakMinutes from Break rule matrix (attendance settings) when paidIn/paidOut change
            // Only apply if user didn't explicitly set paidBreakMinutes in this update
            let effectiveBreak = paidBreak;
            if (updates.paidBreakMinutes === undefined && breakTimeSettings?.rules?.length) {
              const matchingRule = breakTimeSettings.rules.find(
                (r) => grossHours >= r.minHours && grossHours < r.maxHours
              );
              if (matchingRule != null) {
                effectiveBreak = matchingRule.breakMinutes;
                apiUpdates.paidBreakMinutes = matchingRule.breakMinutes;
              }
            }

            // If Paid Break is Unpaid, subtract break minutes; if Paid, don't subtract
            const totalMinutes = Math.max(0, outMinutes - inMinutes - (paidBreakPaid ? 0 : effectiveBreak));
            apiUpdates.totalHours = Math.round((totalMinutes / 60) * 100) / 100;
          }
        }
        
        if (updates.totalHours !== undefined) apiUpdates.totalHours = updates.totalHours;
        
        console.log('[Timecard] handleDayUpdate SENDING:', {
          timecardId: timecard.id,
          dayId: dayData.id,
          dateStr,
          locationId: selectedLocationId,
          apiUpdates: JSON.stringify(apiUpdates)
        });
        await timecardApi.updateDay(timecard.id, dayData.id, apiUpdates);
        // Refetch timecard to ensure we have the latest data for color matching
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        const updatedTimecard = await timecardApi.getByEmployeeAndWeek(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined);
        const updatedDay = updatedTimecard?.days.find(d => d.id === dayData.id);
        console.log('[Timecard] handleDayUpdate AFTER REFETCH:', {
          dateStr,
          locationId: selectedLocationId,
          updatedDay: updatedDay ? {
            id: updatedDay.id,
            date: updatedDay.date,
            mode: updatedDay.mode,
            paidIn: updatedDay.paidIn,
            paidOut: updatedDay.paidOut,
            paidBreakMinutes: updatedDay.paidBreakMinutes,
            totalHours: updatedDay.totalHours
          } : 'NOT FOUND'
        });
        if (updatedTimecard) {
          setTimecard(updatedTimecard);
        } else {
          // Fallback: Update local state if refetch fails
        setTimecard(prev => {
          if (!prev) return null;
          return {
            ...prev,
            days: prev.days.map(d =>
              d.date === dateStr ? { ...d, ...updates } : d
            )
          };
        });
        }
      } catch (error) {
        console.error('Error updating timecard day:', error);
      } finally {
        setSavingDays(prev => {
          const next = new Set(prev);
          if (dayData) next.delete(dayData.id);
          return next;
        });
      }
    }
  };

  // Format time for display
  const formatTime = (timeStr: string | null | undefined): string => {
    if (!timeStr) return '--:--';
    return timeStr;
  };

  // Calculate total hours for the week
  const weeklyTotalHours = useMemo(() => {
    if (!timecard) return 0;
    return timecard.days.reduce((sum, day) => sum + (day.totalHours || 0), 0);
  }, [timecard]);

  // Filter and sort employees by location and name
  const filteredEmployees = useMemo(() => {
    if (!employees || employees.length === 0) {
      return [];
    }
    
    let filtered = [...employees];
    
    // Apply location filter from top banner (if selected)
    if (selectedLocationId !== null && selectedLocationId !== 0) {
      filtered = filtered.filter((employee) => {
        const hasMainLocation = employee.profile?.mainLocationId === selectedLocationId;
        const hasSubLocation =
          employee.profile?.subLocationIds &&
          Array.isArray(employee.profile.subLocationIds) &&
          employee.profile.subLocationIds.includes(selectedLocationId);
        return hasMainLocation || hasSubLocation;
      });
    }
    
    // Apply search filter if query exists
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(emp =>
        `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(query) ||
        emp.email.toLowerCase().includes(query)
      );
    }
    
    // Sort by first name, then last name (handle null/undefined names)
    const sorted = filtered.sort((a, b) => {
      const lastNameA = (a.lastName || '').trim();
      const firstNameA = (a.firstName || '').trim();
      const lastNameB = (b.lastName || '').trim();
      const firstNameB = (b.firstName || '').trim();
      
      // Compare first names first
      const firstNameCompare = firstNameA.toLowerCase().localeCompare(firstNameB.toLowerCase());
      if (firstNameCompare !== 0) {
        return firstNameCompare;
      }
      
      // If first names are the same, compare last names
      return lastNameA.toLowerCase().localeCompare(lastNameB.toLowerCase());
    });
    
    return sorted;
  }, [employees, searchQuery, selectedLocationId]);

  // Reset selection and data when no specific location is selected
  useEffect(() => {
    if (!selectedLocationId || selectedLocationId === 0) {
      if (selectedEmployeeId !== null) {
        setSelectedEmployeeId(null);
      }
      setTimecard(null);
      setShifts([]);
      setAttendanceRecords([]);
    }
  }, [selectedLocationId, selectedEmployeeId]);

  // If location changes and current employee is no longer visible, clear the selection
  useEffect(() => {
    if (
      selectedLocationId !== null &&
      selectedLocationId !== 0 &&
      selectedEmployeeId &&
      !filteredEmployees.some(e => e.id === selectedEmployeeId)
    ) {
      setSelectedEmployeeId(null);
    }
  }, [selectedLocationId, filteredEmployees, selectedEmployeeId]);

  // Scroll to selected employee only on initial load (when employee is remembered from localStorage)
  useEffect(() => {
    // Only scroll on initial load, not when user manually selects an employee
    if (!hasScrolledToInitialEmployee.current && selectedEmployeeId && selectedEmployeeRef.current && employeeListRef.current && filteredEmployees.length > 0) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const container = employeeListRef.current;
        const selectedElement = selectedEmployeeRef.current;
        if (container && selectedElement) {
          const containerRect = container.getBoundingClientRect();
          const elementRect = selectedElement.getBoundingClientRect();
          const scrollTop = container.scrollTop;
          const elementTop = elementRect.top - containerRect.top + scrollTop;
          const elementHeight = elementRect.height;
          
          // Calculate position to show selected employee at 3rd row (2 rows above it)
          // Assuming average row height, we want to show 2 rows above the selected employee
          // Use the actual element height to calculate spacing
          const rowsAbove = 2; // Show 2 rows above (making selected employee the 3rd row)
          const offsetFromTop = rowsAbove * elementHeight;
          
          // Scroll so that the selected employee appears at the 3rd row position
          const targetScroll = elementTop - offsetFromTop;
          container.scrollTo({
            top: Math.max(0, targetScroll),
            behavior: 'smooth'
          });
          
          // Mark that we've done the initial scroll
          hasScrolledToInitialEmployee.current = true;
        }
      }, 100);
    }
  }, [selectedEmployeeId, filteredEmployees]);

  // If no specific location is selected, show info message instead of timecards UI
  if (!selectedLocationId || selectedLocationId === 0) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center text-gray-500 max-w-md text-center px-4">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
            <MapPin className="w-8 h-8 text-blue-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Select a Location to View Timecards
          </h2>
          <p className="text-sm">
            Timecards are scoped by location. Please choose a location from the top location dropdown to see the employee list and their timecards.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50 overflow-hidden">
      {/* Employee List Sidebar */}
      <div data-timecards-sidebar className="w-64 bg-white border-r border-gray-200 flex flex-col h-full">
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Employees</h2>
          {/* Search Box */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        <div 
          ref={employeeListRef}
          className="flex-1 overflow-y-auto min-h-0"
        >
          {filteredEmployees.map(employee => (
            <button
              key={employee.id}
              ref={selectedEmployeeId === employee.id ? selectedEmployeeRef : null}
              onClick={() => setSelectedEmployeeId(employee.id)}
              className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                selectedEmployeeId === employee.id ? 'bg-blue-50 border-blue-200' : ''
              }`}
            >
              <div className="font-medium text-gray-900">
                {employee.firstName} {employee.lastName}
              </div>
              <div className="text-sm text-gray-500">{employee.email}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden h-full">
        {/* Week Navigator */}
        <div className="bg-gray-50 border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Selected Employee Name */}
              {selectedEmployeeId && (() => {
                const selectedEmployee = employees.find(e => e.id === selectedEmployeeId);
                return selectedEmployee ? (
                  <div className="flex items-center space-x-2 px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="text-sm font-medium text-blue-900">
                      {selectedEmployee.firstName} {selectedEmployee.lastName}
                    </span>
                    {timecard?.isApproved && (
                      <span className="text-sm font-semibold text-blue-700 border-l border-blue-300 pl-2 ml-1">
                        {weeklyTotalHours.toFixed(2)} hrs
                      </span>
                    )}
                  </div>
                ) : null;
              })()}
              <button
                onClick={handlePreviousWeek}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={handleTodayWeek}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Today
              </button>
              <button
                onClick={handleNextWeek}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-gray-400" />
                <span className="text-lg font-semibold text-gray-900">
                  {format(weekStart, 'MMM dd')} - {format(weekEnd, 'MMM dd, yyyy')}
                </span>
              </div>
            </div>
            {selectedEmployeeId && timecard && (
              <div className="flex items-center space-x-3">
                {timecard.isApproved ? (
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-green-50 text-green-700 border border-green-200"
                      title="Further changes in attendance or shifts will not sync into this timecard."
                    >
                      Approved
                      <Info className="w-3.5 h-3.5 text-green-600 flex-shrink-0" aria-hidden />
                    </span>
                    <button
                      onClick={() => setShowUnapproveConfirm(true)}
                      disabled={loading}
                      className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Unapprove to allow edits and sync again"
                    >
                      <span>UnApprove</span>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleApproveTimecard}
                    disabled={loading}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>Approve Timecard</span>
                  </button>
                )}
                <button
                  onClick={handleResync}
                  disabled={loading || !!timecard.isApproved}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={timecard.isApproved ? 'Re-sync is disabled for approved timecards.' : 'Re-sync from Shifts and Attendance'}
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  <span>Re-sync</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {timecard?.isApproved && showApprovedBlockMessage && (
          <div className="px-4 pt-2">
            <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" aria-hidden />
              <div className="flex-1">
                <div className="font-semibold">Timecard is approved</div>
                <div className="text-xs sm:text-sm">{APPROVED_BLOCK_MESSAGE}</div>
              </div>
              <button
                type="button"
                onClick={() => setShowApprovedBlockMessage(false)}
                className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full text-amber-500 hover:bg-amber-100 hover:text-amber-700 focus:outline-none"
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          </div>
        )}

        {overlapError && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Time overlap</h3>
                <button
                  type="button"
                  onClick={() => setOverlapError(null)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="px-4 py-4 text-sm text-gray-700">
                <p>{overlapError}</p>
              </div>
              <div className="flex items-center justify-end border-t px-4 py-3">
                <button
                  type="button"
                  onClick={() => setOverlapError(null)}
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteConfirm && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Delete timecard?</h3>
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(null)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="px-4 py-4 space-y-3 text-sm text-gray-700">
                <p>
                  Are you sure you want to delete the timecard for <span className="font-semibold">{deleteConfirm.dateLabel}</span>?
                </p>
                <p>This action cannot be undone.</p>
              </div>
              <div className="flex items-center justify-end gap-3 border-t px-4 py-3">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(null)}
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (deleteConfirm) {
                      await handleDeleteManualTimecard(deleteConfirm.dateStr, deleteConfirm.shiftIndex, deleteConfirm.dayDataId);
                      setDeleteConfirm(null);
                    }
                  }}
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {timecard?.isApproved && showUnapproveConfirm && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Unapprove timecard?</h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowUnapproveConfirm(false);
                    setUnapproveError(null);
                  }}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="px-4 py-4 space-y-3 text-sm text-gray-700">
                <p>
                  This timecard is currently <span className="font-semibold">approved</span>. Unapproving it will
                  allow further edits and syncing from shifts and attendance.
                </p>
                <p>Are you sure you want to unapprove this timecard?</p>
                {unapproveError && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                    {unapproveError}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-3 border-t px-4 py-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowUnapproveConfirm(false);
                    setUnapproveError(null);
                  }}
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUnapproveTimecard}
                  disabled={loading}
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Unapprove timecard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Timecard Grid */}
        <div className="flex-1 overflow-auto p-6">
          {!selectedEmployeeId ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              Select an employee to view timecard
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : timecard ? (
            <div className="bg-gray-50 rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                      <th colSpan={3} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled</th>
                      <th colSpan={3} className="px-4 py-3 pl-5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actual</th>
                      <th colSpan={3} className="px-4 py-3 pl-5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Extras</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                    </tr>
                    <tr>
                      <th></th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500">In</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500">Out</th>
                      <th className="px-2 py-2 pr-5 text-xs font-medium text-gray-500">Break</th>
                      <th className="px-2 py-2 pl-5 text-xs font-medium text-gray-500">In</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500">Out</th>
                      <th className="px-2 py-2 pr-5 text-xs font-medium text-gray-500">Break</th>
                      <th className="px-2 py-2 pl-5 text-xs font-medium text-gray-500">In</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500">Out</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500">Break</th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-50">
                    {tableRows.map((row, index) => {
                      // isUnscheduled is true if attendance exists without matching shift
                      // Also true if no shifts and no attendance for the day
                      const isUnscheduled = row.isUnscheduled || (!row.shift && !row.attendanceRecord);
                      const scheduledIn = row.shift ? extractTime(row.shift.startTime) : null;
                      const scheduledOut = row.shift ? extractTime(row.shift.endTime) : null;
                      const scheduledBreak = row.shift ? getBreakMinutes(row.shift) : null;
                      
                      // Check if this is a new day (different from previous row)
                      const previousRow = index > 0 ? tableRows[index - 1] : null;
                      const isNewDay = !previousRow || previousRow.dateStr !== row.dateStr;
                      
                      // Show date label on the first row for this date (after sorting), not based on shiftIndex
                      const showDateLabel = isNewDay;
                      // Apply prominent border only between days, not between shifts on same day
                      const borderClass = isNewDay ? 'border-t-2 border-gray-400' : '';

                      // Get actual times from TimecardDay (synced data) if available, otherwise fall back to attendance record
                      // Extract time portion from DateTime strings (API now returns DateTime, but UI displays time only)
                      const actualIn = row.dayData?.actualIn 
                        ? extractTime(row.dayData.actualIn)
                        : (row.attendanceRecord ? extractTime(row.attendanceRecord.clockInTime) : null);
                      const actualOut = row.dayData?.actualOut 
                        ? extractTime(row.dayData.actualOut)
                        : (row.attendanceRecord ? extractTime(row.attendanceRecord.clockOutTime) : null);
                      const actualBreak = row.dayData?.actualBreakMinutes ?? row.attendanceRecord?.breakMinutes ?? null;

                      // Get paid values for comparison (extract time from DateTime strings)
                      const paidIn = row.dayData?.paidIn ? extractTime(row.dayData.paidIn) : null;
                      const paidOut = row.dayData?.paidOut ? extractTime(row.dayData.paidOut) : null;
                      const paidBreak = row.dayData?.paidBreakMinutes ?? null;
                      const paidBreakPaid = row.dayData?.paidBreakPaid ?? null;
                      
                      // Check if there are no Actuals. Normally we disable Paid fields when
                      // there are no Actuals, but for manual rows we WANT Paid to be editable
                      // even when there are no Actuals.
                      const hasNoActuals = !actualIn && !actualOut;
                      const disablePaidFields = !row.isManual && hasNoActuals;
                      
                      // Determine default paidBreakPaid value:
                      // - If shift exists: use shift's breakTimePaid
                      // - If unscheduled: use Attendance settings Break Rule Paid/Unpaid
                      const defaultPaidBreakPaid = row.shift 
                        ? (row.shift.breakTimePaid ?? false)
                        : (breakTimeSettings?.isPaid ?? false);
                      
                      // Use existing value if set, otherwise use default
                      const currentPaidBreakPaid = paidBreakPaid !== null ? paidBreakPaid : defaultPaidBreakPaid;

                      // Helper function to normalize time strings for comparison (handles "HH:mm" format)
                      const normalizeTime = (time: string | null | undefined): string | null => {
                        if (!time) return null;
                        // Remove any whitespace and ensure format is consistent
                        return time.trim().replace(/\s+/g, '');
                      };

                      // Compare values to determine if they match paid values
                      const scheduledInMatchesPaid = scheduledIn && paidIn && normalizeTime(scheduledIn) === normalizeTime(paidIn);
                      const actualInMatchesPaid = actualIn && paidIn && normalizeTime(actualIn) === normalizeTime(paidIn);
                      const bothInMatchPaid = scheduledInMatchesPaid && actualInMatchesPaid;
                      // Check which was last clicked for this day
                      const lastClickedIn = lastClickedField.get(row.dateStr);
                      // If both match Paid, prioritize Actual unless Scheduled was clicked
                      const showScheduledInColor = scheduledInMatchesPaid && (!actualInMatchesPaid || (bothInMatchPaid && lastClickedIn === 'scheduledIn'));
                      
                      const scheduledOutMatchesPaid = scheduledOut && paidOut && normalizeTime(scheduledOut) === normalizeTime(paidOut);
                      const actualOutMatchesPaid = actualOut && paidOut && normalizeTime(actualOut) === normalizeTime(paidOut);
                      const bothOutMatchPaid = scheduledOutMatchesPaid && actualOutMatchesPaid;
                      const lastClickedOut = lastClickedField.get(row.dateStr);
                      // If both match Paid, prioritize Actual unless Scheduled was clicked
                      const showScheduledOutColor = scheduledOutMatchesPaid && (!actualOutMatchesPaid || (bothOutMatchPaid && lastClickedOut === 'scheduledOut'));
                      
                      const scheduledBreakMatchesPaid = scheduledBreak !== null && paidBreak !== null && scheduledBreak === paidBreak;
                      const actualBreakMatchesPaid = actualBreak !== null && paidBreak !== null && actualBreak === paidBreak;
                      const bothBreakMatchPaid = scheduledBreakMatchesPaid && actualBreakMatchesPaid;
                      const lastClickedBreak = lastClickedField.get(row.dateStr);
                      // If both match Paid, prioritize Actual unless Scheduled was clicked
                      const showScheduledBreakColor = scheduledBreakMatchesPaid && (!actualBreakMatchesPaid || (bothBreakMatchPaid && lastClickedBreak === 'scheduledBreak'));

                      // Check if both Actual IN and Actual Out have values (required for clickability)
                      const canClick = actualIn && actualOut;

                      // Handler to copy value to Paid box
                      const handleCopyToPaid = (value: string | null | number, field: 'paidIn' | 'paidOut' | 'paidBreakMinutes', source: 'scheduledIn' | 'actualIn' | 'scheduledOut' | 'actualOut' | 'scheduledBreak' | 'actualBreak') => {
                        if (!canClick || value === null || value === undefined) return;
                        // Track which field was clicked
                        setLastClickedField(prev => {
                          const newMap = new Map(prev);
                          newMap.set(row.dateStr, source);
                          return newMap;
                        });
                        // Use row.dayData?.id to ensure we update the correct dayData record
                        const dayDataId = row.dayData?.id;
                        if (field === 'paidBreakMinutes') {
                          handleDayUpdate(row.dateStr, row.shift?.id, { [field]: typeof value === 'number' ? value : parseInt(String(value)) || 0 }, dayDataId);
                        } else {
                          handleDayUpdate(row.dateStr, row.shift?.id, { [field]: value }, dayDataId);
                        }
                      };

                      return (
                        <tr key={`${row.dateStr}-${row.shiftIndex}`} className={`hover:bg-gray-50 ${borderClass}`}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {showDateLabel ? format(row.date, 'EEEE MMM dd') : ''}
                          </td>
                          {/* Scheduled - keep completely blank for manual rows */}
                          {row.isManual ? (
                            <>
                              <td className="px-2 py-3 whitespace-nowrap" colSpan={3}></td>
                            </>
                          ) : (
                            <>
                              <td className={`px-2 py-3 whitespace-nowrap ${isUnscheduled ? 'text-center pr-5' : ''} ${!isUnscheduled ? 'relative' : ''}`} colSpan={isUnscheduled ? 3 : 1}>
                            {isUnscheduled ? (
                                  <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium inline-block">
                                Unscheduled
                              </div>
                            ) : (
                                  <div 
                                    onClick={() => canClick && scheduledIn && handleCopyToPaid(scheduledIn, 'paidIn', 'scheduledIn')}
                                    className={`px-2 py-1 text-sm border rounded text-center ${
                                      showScheduledInColor 
                                        ? 'border-gray-300 bg-green-100 text-green-900'
                                        : 'border-gray-300 bg-gray-50 text-gray-900'
                                    } ${canClick && scheduledIn ? 'cursor-pointer' : ''}`}
                                    title={canClick && scheduledIn ? 'Click to copy to Paid In' : ''}
                                  >
                                    {scheduledIn || '--:--'}
                                  </div>
                            )}
                            {!isUnscheduled && (
                              <div className="absolute top-1/2 left-[calc(100%-0.25rem)] w-3 h-0.5 border-t-2 border-dotted border-gray-400 transform -translate-y-1/2 z-10"></div>
                            )}
                          </td>
                          {!isUnscheduled && (
                            <>
                              <td className="px-2 py-3 whitespace-nowrap">
                                    <div 
                                      onClick={() => canClick && scheduledOut && handleCopyToPaid(scheduledOut, 'paidOut', 'scheduledOut')}
                                      className={`px-2 py-1 text-sm border rounded text-center ${
                                        showScheduledOutColor 
                                          ? 'border-gray-300 bg-red-100 text-red-900'
                                          : 'border-gray-300 bg-gray-50 text-gray-900'
                                      } ${canClick && scheduledOut ? 'cursor-pointer' : ''}`}
                                      title={canClick && scheduledOut ? 'Click to copy to Paid Out' : ''}
                                    >
                                      {scheduledOut || '--:--'}
                                    </div>
                              </td>
                                  <td className="px-2 py-3 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <div 
                                        onClick={() => canClick && scheduledBreak !== null && handleCopyToPaid(scheduledBreak, 'paidBreakMinutes', 'scheduledBreak')}
                                        className={`px-2 py-1 text-sm border rounded text-center ${
                                          showScheduledBreakColor 
                                            ? 'border-gray-300 bg-yellow-100 text-yellow-900'
                                            : 'border-gray-300 bg-gray-50 text-gray-900'
                                        } ${canClick && scheduledBreak !== null ? 'cursor-pointer' : ''}`}
                                        title={canClick && scheduledBreak !== null ? 'Click to copy to Paid Break' : ''}
                                      >
                                        {scheduledBreak || 0}
                                      </div>
                                      {row.shift && (
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                          row.shift.breakTimePaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                          {row.shift.breakTimePaid ? 'Paid' : 'Unpaid'}
                                        </span>
                                      )}
                                    </div>
                              </td>
                            </>
                          )}
                            </>
                          )}
                          {/* Actual - Empty for manual rows */}
                          {row.isManual ? (
                            <>
                              <td className="px-2 py-3 whitespace-nowrap pl-5 border-l-2 border-gray-300" colSpan={3}>
                                {/* Empty - Paid boxes are shown in Scheduled section for manual rows */}
                          </td>
                            </>
                          ) : (
                            <>
                              <td className="px-2 py-3 whitespace-nowrap pl-5 border-l-2 border-gray-300 relative">
                                <div 
                                  onClick={() => canClick && actualIn && handleCopyToPaid(actualIn, 'paidIn', 'actualIn')}
                                  className={`px-2 py-1 text-sm border rounded text-center ${
                                    actualInMatchesPaid && (!bothInMatchPaid || lastClickedIn !== 'scheduledIn')
                                      ? 'border-gray-300 bg-green-100 text-green-900'
                                      : 'border-gray-300 bg-gray-50 text-gray-900'
                                  } ${canClick && actualIn ? 'cursor-pointer' : ''}`}
                                  title={canClick && actualIn ? 'Click to copy to Paid In' : ''}
                                >
                                  {formatTime(actualIn)}
                                </div>
                                <div className="absolute top-1/2 left-[calc(100%-0.25rem)] w-3 h-0.5 border-t-2 border-dotted border-gray-400 transform -translate-y-1/2 z-10"></div>
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap">
                                <div 
                                  onClick={() => canClick && actualOut && handleCopyToPaid(actualOut, 'paidOut', 'actualOut')}
                                  className={`px-2 py-1 text-sm border rounded text-center ${
                                    actualOutMatchesPaid && (!bothOutMatchPaid || lastClickedOut !== 'scheduledOut')
                                      ? 'border-gray-300 bg-red-100 text-red-900'
                                      : 'border-gray-300 bg-gray-50 text-gray-900'
                                  } ${canClick && actualOut ? 'cursor-pointer' : ''}`}
                                  title={canClick && actualOut ? 'Click to copy to Paid Out' : ''}
                                >
                                  {formatTime(actualOut)}
                                </div>
                          </td>
                              <td className="px-2 py-3 whitespace-nowrap pr-5">
                                <div 
                                  onClick={() => canClick && actualBreak !== null && actualBreak !== undefined && handleCopyToPaid(actualBreak, 'paidBreakMinutes', 'actualBreak')}
                                  className={`px-2 py-1 text-sm border rounded text-center ${
                                    actualBreakMatchesPaid && (!bothBreakMatchPaid || lastClickedBreak !== 'scheduledBreak')
                                      ? 'border-gray-300 bg-yellow-100 text-yellow-900'
                                      : 'border-gray-300 bg-gray-50 text-gray-900'
                                  } ${canClick && actualBreak !== null && actualBreak !== undefined ? 'cursor-pointer' : ''}`}
                                  title={canClick && actualBreak !== null && actualBreak !== undefined ? 'Click to copy to Paid Break' : ''}
                                >
                                  {actualBreak !== null && actualBreak !== undefined ? actualBreak : ''}
                                </div>
                              </td>
                            </>
                          )}
                          {/* Paid */}
                          <td className="px-2 py-3 whitespace-nowrap pl-5 border-l-2 border-gray-300">
                            <input
                              key={`paid-in-${row.dateStr}-${row.shiftIndex}-${row.dayData?.id ?? 0}-${paidIn ?? ''}`}
                              type="time"
                              defaultValue={paidIn && paidIn !== '--:--' ? paidIn : ''}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value) {
                                  handleDayUpdate(row.dateStr, row.shift?.id, { paidIn: value }, row.dayData?.id);
                                }
                              }}
                              // Manual rows should always allow Paid editing, even with no Actuals
                              disabled={!row.isManual && hasNoActuals}
                              className={`w-24 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                paidIn && paidIn !== '--:--' ? 'border-gray-300 border-b-4 border-b-green-300' : 'border-gray-300'
                              } ${(!row.isManual && hasNoActuals) ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                              placeholder="--:--"
                            />
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap">
                            <input
                              key={`paid-out-${row.dateStr}-${row.shiftIndex}-${row.dayData?.id ?? 0}-${paidOut ?? ''}`}
                              type="time"
                              defaultValue={paidOut && paidOut !== '--:--' ? paidOut : ''}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value) {
                                  handleDayUpdate(row.dateStr, row.shift?.id, { paidOut: value }, row.dayData?.id);
                                }
                              }}
                              disabled={!row.isManual && hasNoActuals}
                              className={`w-24 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                paidOut && paidOut !== '--:--' ? 'border-gray-300 border-b-4 border-b-red-300' : 'border-gray-300'
                              } ${(!row.isManual && hasNoActuals) ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                              placeholder="--:--"
                            />
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="relative">
                                <input
                                  key={`paid-break-${row.dateStr}-${row.shiftIndex}-${row.dayData?.id ?? 0}-${row.dayData?.paidBreakMinutes ?? 0}`}
                                  type="number"
                                  min="0"
                                  value={row.dayData?.paidBreakMinutes ?? ''}
                                  onChange={(e) => {
                                    handleDayUpdate(row.dateStr, row.shift?.id, { paidBreakMinutes: parseInt(e.target.value) || 0 }, row.dayData?.id);
                                  }}
                                  disabled={!row.isManual && hasNoActuals}
                                  className={`w-20 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    row.dayData?.paidBreakMinutes ? 'border-gray-300 border-b-4 border-b-yellow-300' : 'border-gray-300'
                                  } ${(!row.isManual && hasNoActuals) ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                                />
                                <div className="absolute top-1/2 left-full w-2 h-0.5 border-t-2 border-dotted border-gray-400 transform -translate-y-1/2 z-10"></div>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!(!row.isManual && hasNoActuals)) {
                                      const newPaidBreakPaid = !currentPaidBreakPaid;
                                      handleDayUpdate(row.dateStr, row.shift?.id, { paidBreakPaid: newPaidBreakPaid }, row.dayData?.id);
                                    }
                                  }}
                                  disabled={!row.isManual && hasNoActuals}
                                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                    currentPaidBreakPaid ? 'bg-blue-600' : 'bg-neutral-300'
                                  } ${(!row.isManual && hasNoActuals) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                                  title={(!row.isManual && hasNoActuals) ? 'No Actuals - Cannot edit' : (currentPaidBreakPaid ? 'Break Paid' : 'Break Unpaid')}
                                >
                                  <span
                                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                      currentPaidBreakPaid ? 'translate-x-4.5' : 'translate-x-0.5'
                                    }`}
                                  />
                                </button>
                                <span className={`text-xs font-medium ${
                                  currentPaidBreakPaid ? 'text-green-700' : 'text-red-700'
                                }`}>
                                  {currentPaidBreakPaid ? 'Paid' : 'Unpaid'}
                                </span>
                              </div>
                            </div>
                          </td>
                          {/* Schedule */}
                          <td className="px-4 py-3 whitespace-nowrap pl-5 border-l-2 border-gray-300">
                            {(() => {
                              // Get default schedule code from shift if dayData doesn't have one
                              const defaultScheduleCode = row.dayData?.scheduleCode || 
                                (row.shift?.schedule ? getTwoLetterCode(row.shift.schedule.name) : '');
                              const selectedSchedule = availableSchedules.find(s => 
                                getTwoLetterCode(s.name) === defaultScheduleCode
                              ) || (row.shift?.schedule ? schedules.find(s => s.id === row.shift!.schedule!.id) : null);
                              const scheduleTooltip = selectedSchedule ? selectedSchedule.name : 
                                (row.shift?.schedule ? row.shift.schedule.name : 
                                (defaultScheduleCode ? `Schedule: ${defaultScheduleCode}` : ''));
                              const rowKey = `${row.dateStr}-${row.shiftIndex}`;
                              const isOpen = openScheduleDropdown === rowKey;

                              return (
                                <div className="relative">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenScheduleDropdown(isOpen ? null : rowKey);
                                    }}
                                    className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer bg-white text-center hover:bg-gray-50"
                                    title={scheduleTooltip}
                                  >
                                    {defaultScheduleCode || ''}
                                  </button>
                                  {isOpen && (
                                    <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                      <div
                                        className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDayUpdate(row.dateStr, row.shift?.id, { scheduleCode: undefined });
                                          setOpenScheduleDropdown(null);
                                        }}
                                      >
                                        <div className="font-semibold text-gray-900">Clear</div>
                                        <div className="text-xs text-gray-500">Remove schedule</div>
                                      </div>
                                      {availableSchedules.map(schedule => {
                                        const code = getTwoLetterCode(schedule.name);
                                        const isSelected = code === defaultScheduleCode;
                                        return (
                                          <div
                                            key={schedule.id}
                                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                                              isSelected ? 'bg-blue-50' : ''
                                            }`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDayUpdate(row.dateStr, row.shift?.id, { scheduleCode: code });
                                              setOpenScheduleDropdown(null);
                                            }}
                                          >
                                            <div className="font-semibold text-gray-900">{code}</div>
                                            <div className="text-xs text-gray-500">{schedule.name}</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          {/* Position */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {(() => {
                              // Get default position code from shift if dayData doesn't have one
                              const defaultPositionCode = row.dayData?.positionCode || 
                                (row.shift?.position ? getTwoLetterCode(row.shift.position.title) : '');
                              const selectedPosition = availablePositions.find(p => 
                                getTwoLetterCode(p.title) === defaultPositionCode
                              ) || (row.shift?.position ? positions.find(p => p.id === row.shift!.position!.id) : null);
                              const positionTooltip = selectedPosition ? selectedPosition.title : 
                                (row.shift?.position ? row.shift.position.title : 
                                (defaultPositionCode ? `Position: ${defaultPositionCode}` : ''));
                              const rowKey = `${row.dateStr}-${row.shiftIndex}`;
                              const isOpen = openPositionDropdown === rowKey;

                              return (
                                <div className="relative">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenPositionDropdown(isOpen ? null : rowKey);
                                    }}
                                    className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer bg-white text-center hover:bg-gray-50"
                                    title={positionTooltip}
                                  >
                                    {defaultPositionCode || ''}
                                  </button>
                                  {isOpen && (
                                    <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                      <div
                                        className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDayUpdate(row.dateStr, row.shift?.id, { positionCode: undefined });
                                          setOpenPositionDropdown(null);
                                        }}
                                      >
                                        <div className="font-semibold text-gray-900">Clear</div>
                                        <div className="text-xs text-gray-500">Remove position</div>
                                      </div>
                                      {availablePositions.map(position => {
                                        const code = getTwoLetterCode(position.title);
                                        const isSelected = code === defaultPositionCode;
                                        return (
                                          <div
                                            key={position.id}
                                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                                              isSelected ? 'bg-blue-50' : ''
                                            }`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDayUpdate(row.dateStr, row.shift?.id, { positionCode: code });
                                              setOpenPositionDropdown(null);
                                            }}
                                          >
                                            <div className="font-semibold text-gray-900">{code}</div>
                                            <div className="text-xs text-gray-500">{position.title}</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          {/* Extras */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={row.dayData?.extras || 0}
                              onChange={(e) => {
                                handleDayUpdate(row.dateStr, row.shift?.id, { extras: parseFloat(e.target.value) || 0 });
                              }}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-orange-600 font-medium"
                            />
                          </td>
                          {/* Total Hours */}
                          <td className="px-4 py-3 whitespace-nowrap relative">
                            <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-blue-600">
                              {(row.dayData?.totalHours || 0).toFixed(2)}
                            </span>
                            {row.dayData && savingDays.has(row.dayData.id) && (
                                <Clock className="w-4 h-4 text-gray-400 animate-spin" />
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const rowKey = `${row.dateStr}-${row.shiftIndex}`;
                                  setOpenMenuRow(openMenuRow === rowKey ? null : rowKey);
                                }}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                                title="Options"
                              >
                                <MoreVertical className="w-4 h-4 text-gray-500" />
                              </button>
                              {openMenuRow === `${row.dateStr}-${row.shiftIndex}` && (
                                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 min-w-[150px]">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddManualTimecard(row.dateStr);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                  >
                                    Add Timecard
                                  </button>
                                  {row.isManual && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteConfirm({
                                          dateStr: row.dateStr,
                                          shiftIndex: row.shiftIndex,
                                          dayDataId: row.dayData?.id,
                                          dateLabel: format(row.date, 'EEEE MMM dd'),
                                        });
                                        setOpenMenuRow(null);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      Delete Timecard
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {/* Weekly Total Row */}
                    <tr className="bg-gray-50 font-semibold">
                      <td colSpan={13} className="px-4 py-3 text-sm text-gray-900">
                        Weekly Total Hours:
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
                        {weeklyTotalHours.toFixed(2)}
                      </td>
                    </tr>
                    {/* Other locations' weekly totals (if any > 0) */}
                    {otherLocationTotals.map(({ locationId, locationName, totalHours }) => (
                      <tr key={locationId} className="bg-gray-50/80">
                        <td colSpan={13} className="px-4 py-2 text-sm text-gray-600 pl-8">
                          {locationName}:
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-600">
                          {totalHours.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Sync Step 1 Modal - Show Shifts Report */}
      {showSyncStep1Modal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-50 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Sync Step 1: Shifts Report</h2>
              <button
                onClick={async () => {
                  setShowSyncStep1Modal(false);
                  // Refetch timecard data after closing modal to reflect DB changes
                  await refetchTimecardData();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Found {syncStep1Data.length} shift(s), {syncStep2Data.length} attendance record(s), {syncStep3Data.length} processed record(s), {syncStep4Data.length} matched record(s), {syncStep5Data.length} unmatched record(s), {syncStep7Data.length} date record(s), {syncStep8Data.length} comparison record(s), {syncStep9Data.length} paid calculation record(s), and {syncStep10Data.length} final status record(s) for the selected employee and week.
              </p>
            </div>

            {/* Step 1: Shifts */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 1: Shifts</h3>
              {syncStep1Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">In Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Out Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Break Paid</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-50 divide-y divide-gray-200">
                      {syncStep1Data.map((shift, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{shift.shiftId}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{shift.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{shift.inTime}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{shift.outTime}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{shift.breakMins}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              shift.breakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {shift.breakPaid ? 'Paid' : 'Unpaid'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No shifts found for this employee and week.
                </div>
              )}
            </div>

            {/* Step 2: Attendance Records */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 2: Actual Times (Attendance Records)</h3>
              {syncStep2Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendance ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">In Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Out Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adjusted In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adjusted Out</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-50 divide-y divide-gray-200">
                      {syncStep2Data.map((attendance, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{attendance.attendanceId}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{attendance.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{attendance.inTime}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{attendance.outTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{attendance.adjustedInTime || '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{attendance.adjustedOutTime || '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No attendance records found for this employee and week.
                </div>
              )}
            </div>

            {/* Step 3: Processed Records (Adjusted times merged into In/Out times) */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 3: Processed Records (Adjusted times merged)</h3>
              {syncStep3Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendance ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">In Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Out Time</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-50 divide-y divide-gray-200">
                      {syncStep3Data.map((record, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.attendanceId}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.inTime}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.outTime || '--:--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No processed records found.
                </div>
              )}
            </div>

            {/* Step 4: Matched Records (Step 1 shifts with overlapping Step 3 attendance) */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 4: Matched Records (Shifts with Overlapping Attendance)</h3>
              {syncStep4Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matched Count</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-50 divide-y divide-gray-200">
                      {syncStep4Data.map((record, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftId}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftDate}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftInTime}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftOutTime}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftBreakMins}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              record.shiftBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {record.shiftBreakPaid ? 'Paid' : 'Unpaid'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime ? record.actualInDate : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime ? record.actualOutDate : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualBreakMins !== null ? record.actualBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.matchedAttendanceCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No matched records found.
                </div>
              )}
            </div>

            {/* Step 5: Unmatched Records (Step 3 records without matching Step 1 shifts) */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 5: Unmatched Records (Attendance without Matching Shifts)</h3>
              {syncStep5Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matched Count</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-50 divide-y divide-gray-200">
                      {syncStep5Data.map((record, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftId ?? '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftDate ?? '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftInTime ?? '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftOutTime ?? '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftBreakMins ?? '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.shiftBreakPaid !== null ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.shiftBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.shiftBreakPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime ? record.actualInDate : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime ? record.actualOutDate : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualBreakMins !== null ? record.actualBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.matchedAttendanceCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No unmatched records found.
                </div>
              )}
            </div>

            {/* Step 7: Date-based Records with Hash */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 7: Date-based Records with Hash</h3>
              {syncStep7Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hash</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-50 divide-y divide-gray-200">
                      {syncStep7Data.map((record, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftBreakMins !== null ? record.shiftBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.shiftBreakPaid !== null ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.shiftBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.shiftBreakPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualBreakMins !== null ? record.actualBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-xs break-all max-w-xs">{record.hash}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No date records found.
                </div>
              )}
            </div>

            {/* Step 8: Comparison Records with MatchType */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 8: Comparison Records with MatchType</h3>
              {syncStep8Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hash</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MatchType</th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-50 divide-y divide-gray-200">
                      {syncStep8Data.map((record, index) => (
                        <tr key={index} className={`hover:bg-gray-50 ${
                          record.matchType === 'Match' ? 'bg-green-50' :
                          record.matchType === 'NO_DB_Hash' ? 'bg-yellow-50' :
                          record.matchType === 'NO_NEW_Hash' ? 'bg-red-50' : ''
                        }`}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftBreakMins !== null ? record.shiftBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.shiftBreakPaid !== null ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.shiftBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.shiftBreakPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualBreakMins !== null ? record.actualBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-xs break-all max-w-xs">{record.hash}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              record.matchType === 'Match' ? 'bg-green-100 text-green-800' :
                              record.matchType === 'NO_DB_Hash' ? 'bg-yellow-100 text-yellow-800' :
                              record.matchType === 'NO_NEW_Hash' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {record.matchType}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No comparison records found.
                </div>
              )}
            </div>

            {/* Step 9: Paid Calculation Records */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 9: Paid Calculation Records</h3>
              {syncStep9Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hash</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MatchType</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Break Paid</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {syncStep9Data.map((record, index) => (
                        <tr key={index} className={`hover:bg-gray-50 ${
                          record.matchType === 'Match' ? 'bg-green-50' :
                          record.matchType === 'NO_DB_Hash' ? 'bg-yellow-50' :
                          record.matchType === 'NO_NEW_Hash' ? 'bg-red-50' : ''
                        }`}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftBreakMins !== null ? record.shiftBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.shiftBreakPaid !== null ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.shiftBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.shiftBreakPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualBreakMins !== null ? record.actualBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-xs break-all max-w-xs">{record.hash}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              record.matchType === 'Match' ? 'bg-green-100 text-green-800' :
                              record.matchType === 'NO_DB_Hash' ? 'bg-yellow-100 text-yellow-800' :
                              record.matchType === 'NO_NEW_Hash' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {record.matchType}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">{record.paidInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">{record.paidOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">{record.paidBreakMins !== null ? record.paidBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.paidBreakPaid !== null ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.paidBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.paidBreakPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No paid calculation records found.
                </div>
              )}
            </div>

            {/* Step 10: Final Status Records (Inserted/Deleted/Not Touched) */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Step 10: Final Status Records</h3>
              {syncStep10Data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift Break Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hash</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MatchType</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Break (mins)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Break Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Hours</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {syncStep10Data
                        .slice()
                        .sort((a, b) => {
                          // First sort by date
                          const dateA = a.date || '';
                          const dateB = b.date || '';
                          if (dateA !== dateB) {
                            return dateA.localeCompare(dateB);
                          }
                          
                          // Within the same date, sort by ActualIn time if available, otherwise by ShiftIn time
                          const getSortTime = (record: typeof a): string => {
                            // If ActualIn exists, use it for sorting
                            if (record.actualInTime && record.actualInTime !== '--:--') {
                              return `${record.date}T${record.actualInTime}:00`;
                            }
                            // Otherwise use ShiftIn time
                            if (record.shiftInTime && record.shiftInTime !== '--:--') {
                              return `${record.date}T${record.shiftInTime}:00`;
                            }
                            // Fallback: use date only (will sort these last)
                            return `${record.date}T00:00:00`;
                          };
                          
                          const timeA = getSortTime(a);
                          const timeB = getSortTime(b);
                          return timeA.localeCompare(timeB);
                        })
                        .map((record, index) => (
                        <tr key={index} className={`hover:bg-gray-50 ${
                          record.matchType === 'Match' ? 'bg-green-50' :
                          record.matchType === 'NO_DB_Hash' ? 'bg-yellow-50' :
                          record.matchType === 'NO_NEW_Hash' ? 'bg-red-50' : ''
                        }`}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.shiftBreakMins !== null ? record.shiftBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.shiftBreakPaid !== null ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.shiftBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.shiftBreakPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{record.actualBreakMins !== null ? record.actualBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-xs break-all max-w-xs">{record.hash}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              record.matchType === 'Match' ? 'bg-green-100 text-green-800' :
                              record.matchType === 'NO_DB_Hash' ? 'bg-yellow-100 text-yellow-800' :
                              record.matchType === 'NO_NEW_Hash' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {record.matchType}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">{record.paidInTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">{record.paidOutTime || '--:--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">{record.paidBreakMins !== null ? record.paidBreakMins : '--'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {record.paidBreakPaid !== null ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.paidBreakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.paidBreakPaid ? 'Paid' : 'Unpaid'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">
                            {record.totalHours !== null ? record.totalHours.toFixed(2) : '--'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium font-semibold ${
                              record.status === 'Inserted' ? 'bg-green-100 text-green-800' :
                              record.status === 'Deleted' ? 'bg-red-100 text-red-800' :
                              record.status === 'Not Touched' ? 'bg-gray-100 text-gray-800' :
                              record.status?.includes('Failed') ? 'bg-orange-100 text-orange-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {record.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border rounded">
                  No final status records found.
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={async () => {
                  setShowSyncStep1Modal(false);
                  // Refetch timecard data after closing modal to reflect DB changes
                  await refetchTimecardData();
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timecards;
